'use client';

import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { useAuth } from '@/contexts/auth-context';
import { usePermissions } from '@/hooks/use-permissions';
import { getSocket } from '@/lib/echo';
import { apiClient } from '@/lib/api/client';
import { notificationKeys } from '@/features/notifications';
import { systemLogKeys } from '@/features/system-logs';

/**
 * Live dashboard updates over Reverb, replacing the 5-second `/dashboard/stream`
 * poll.
 *
 * Every event is a bare "something changed" signal — the handler invalidates a
 * TanStack query and lets REST return the current state. Queued broadcasts have
 * no ordering guarantee, so trusting a payload could overwrite fresher data
 * with staler data; a refetch is self-correcting by construction.
 *
 * `system-logs` is subscribed only when the user holds the permission.
 * Otherwise `/broadcasting/auth` would 403 on every mount for every user who
 * cannot read logs.
 *
 * This is the only live path — the `/dashboard/stream` SSE hook it replaced is
 * gone. When the socket is down, `useEchoConnection().shouldFallback` (read in
 * `components/layout/header.tsx`) re-enables the polling queries instead.
 */
export function useDashboardLive(): void {
  const { user, status } = useAuth();
  const { hasPermission } = usePermissions();
  const queryClient = useQueryClient();

  const userId = user?.id;
  const authenticated = status === 'authenticated';
  const canViewLogs = hasPermission('system_logs.view');

  // Per-user channel: unread-notification count and permission changes.
  useEffect(() => {
    if (!authenticated || !userId) return;

    let cancelled = false;
    const channelName = `App.Models.User.${userId}`;

    void getSocket()
      .then((socket) => {
        if (cancelled) return;
        const channel = socket.private(channelName);

        channel.listen('.notification-count-changed', () => {
          void queryClient.invalidateQueries({
            queryKey: notificationKeys.unreadCount()
          });
        });

        channel.listen('.permissions-changed', () => {
          // Clear the stored hash BEFORE triggering the refresh. The refetch's
          // own response carries the new X-Permissions-Hash; with the stored
          // value null, processPermissionsHash re-baselines silently instead
          // of firing a second, redundant refresh.
          apiClient.resetPermissionsHash();
          window.dispatchEvent(new CustomEvent('permissions-changed'));
        });
      })
      .catch(() => {
        // Socket unavailable — the existing polling path still covers this.
      });

    return () => {
      cancelled = true;
      void getSocket()
        .then((socket) => socket.leave(channelName))
        .catch(() => {});
    };
  }, [authenticated, userId, queryClient]);

  // Shared audit-log channel. Global rather than per-user: audit_logs has no
  // tenant column and the latest-logs query is unscoped, so every viewer reads
  // the same rows and one broadcast serves all of them.
  useEffect(() => {
    if (!authenticated || !canViewLogs) return;

    let cancelled = false;

    void getSocket()
      .then((socket) => {
        if (cancelled) return;
        socket.private('system-logs').listen('.audit-log-recorded', () => {
          void queryClient.invalidateQueries({
            queryKey: systemLogKeys.latest()
          });
        });
      })
      .catch(() => {});

    return () => {
      cancelled = true;
      void getSocket()
        .then((socket) => socket.leave('system-logs'))
        .catch(() => {});
    };
  }, [authenticated, canViewLogs, queryClient]);
}
